import { useTranslation } from 'react-i18next';

type Era = { num: string; titleKey: string; descKey: string; img: string; altKey: string };

const eras: Era[] = [
  {
    num: '01',
    titleKey: 'butlerSchool.history.era1.title',
    descKey: 'butlerSchool.history.era1.desc',
    img: '/images/generated/advanced-candlelight-2026.jpg',
    altKey: 'butlerSchool.history.era1.imgAlt',
  },
  {
    num: '02',
    titleKey: 'butlerSchool.history.era2.title',
    descKey: 'butlerSchool.history.era2.desc',
    img: '/images/generated/essential-manners-2026.jpg',
    altKey: 'butlerSchool.history.era2.imgAlt',
  },
  {
    num: '03',
    titleKey: 'butlerSchool.history.era3.title',
    descKey: 'butlerSchool.history.era3.desc',
    img: '/images/generated/advanced-glass-polish-2026.jpg',
    altKey: 'butlerSchool.history.era3.imgAlt',
  },
];

function splitParagraphs(text: string): string[] {
  const parts = text.split(/\n{2,}/).filter((part) => part.trim().length > 0);
  return parts.length > 0 ? parts : [text];
}

export default function HistoryTimeline() {
  const { t } = useTranslation();

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            {t('butlerSchool.history.label')}
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('butlerSchool.history.title')}
          </h2>
          <p className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">
            {t('butlerSchool.history.subtitleEn')}
          </p>
          <div className="gold-line mx-auto mb-6"></div>
          {splitParagraphs(t('butlerSchool.history.lead')).map((paragraph, index) => (
            <p
              key={index}
              className="text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed max-w-[680px] mx-auto"
            >
              {paragraph}
            </p>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {eras.map((era) => (
            <article
              key={era.num}
              className="card-glow bg-background-100/60 border border-background-200/60 flex flex-col cursor-default"
            >
              <img
                src={era.img}
                alt={t(era.altKey)}
                className="w-full aspect-[4/3] object-cover object-center"
                loading="lazy"
              />
              <div className="p-6 md:p-7 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="section-title-en text-accent-500 text-[15px] tracking-[0.16em]">
                    {era.num}
                  </span>
                  <span className="flex-1 h-px bg-accent-500/30"></span>
                </div>
                <h3 className="font-heading text-[18px] md:text-[19px] text-foreground-900 tracking-[0.05em] leading-[1.7] mb-4">
                  {t(era.titleKey)}
                </h3>
                {splitParagraphs(t(era.descKey)).map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-[16px] text-foreground-600 tracking-[0.03em] leading-[1.95]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>

        {splitParagraphs(t('butlerSchool.history.note')).map((paragraph, index) => (
          <p
            key={index}
            className="text-[13px] text-foreground-400 tracking-[0.04em] leading-relaxed max-w-[900px] mx-auto mt-10 text-center"
          >
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  );
}
